import { create } from 'zustand';
import { getTaskChecklistItems } from '../api/generated/task-checklist-items/task-checklist-items';
import type {
  FacilityFlowCoreModulesPlanningDtosChecklistItemsTaskChecklistItemDto,
  FacilityFlowCoreModulesPlanningDtosChecklistItemsCreateChecklistItemRequest,
  FacilityFlowCoreModulesPlanningDtosChecklistItemsUpdateChecklistItemRequest,
} from '../api/generated/models';

const {
  getApiV1TasksTaskIdChecklistItems,
  postApiV1TasksTaskIdChecklistItems,
  putApiV1TasksTaskIdChecklistItemsItemId,
  deleteApiV1TasksTaskIdChecklistItemsItemId,
  putApiV1TasksTaskIdChecklistItemsOrder,
} = getTaskChecklistItems();

export type ChecklistItemDto = FacilityFlowCoreModulesPlanningDtosChecklistItemsTaskChecklistItemDto;
export type CreateChecklistItemRequest =
  FacilityFlowCoreModulesPlanningDtosChecklistItemsCreateChecklistItemRequest;
export type UpdateChecklistItemRequest =
  FacilityFlowCoreModulesPlanningDtosChecklistItemsUpdateChecklistItemRequest;

/** Column limits mirrored from the backend so the form fails before the request does. */
export const CHECKLIST_TITLE_MAX = 300;
export const CHECKLIST_INTERVAL_HINT_MAX = 100;

interface TaskChecklistState {
  items: ChecklistItemDto[];
  loading: boolean;
  error: string | null;

  fetchItems: (taskId: string, includeInactive?: boolean) => Promise<void>;
  createItem: (taskId: string, data: CreateChecklistItemRequest) => Promise<ChecklistItemDto>;
  updateItem: (taskId: string, itemId: string, data: UpdateChecklistItemRequest) => Promise<ChecklistItemDto>;
  deleteItem: (taskId: string, itemId: string) => Promise<void>;
  reorderItems: (taskId: string, itemIds: string[]) => Promise<void>;
  clearItems: () => void;
}

export const useTaskChecklistStore = create<TaskChecklistState>((set, get) => ({
  items: [],
  loading: false,
  error: null,

  fetchItems: async (taskId, includeInactive = true) => {
    set({ loading: true, error: null });
    try {
      const data = await getApiV1TasksTaskIdChecklistItems(taskId, { includeInactive });
      set({ items: data || [], loading: false });
    } catch {
      set({ error: 'tasks:checklist.errors.fetchFailed', loading: false });
    }
  },

  createItem: async (taskId, data) => {
    set({ loading: true, error: null });
    try {
      const created = await postApiV1TasksTaskIdChecklistItems(taskId, data);
      await get().fetchItems(taskId);
      set({ loading: false });
      return created;
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  updateItem: async (taskId, itemId, data) => {
    set({ loading: true, error: null });
    try {
      const updated = await putApiV1TasksTaskIdChecklistItemsItemId(taskId, itemId, data);
      await get().fetchItems(taskId);
      set({ loading: false });
      return updated;
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  deleteItem: async (taskId, itemId) => {
    set({ loading: true, error: null });
    try {
      await deleteApiV1TasksTaskIdChecklistItemsItemId(taskId, itemId);
      await get().fetchItems(taskId);
      set({ loading: false });
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  reorderItems: async (taskId, itemIds) => {
    // Optimistic: the row jumps immediately, the server only confirms the order.
    const previous = get().items;
    const byId = new Map(previous.map((i) => [i.id, i]));
    const reordered = itemIds.map((id) => byId.get(id)).filter((i): i is ChecklistItemDto => !!i);
    set({ items: reordered, error: null });

    try {
      const data = await putApiV1TasksTaskIdChecklistItemsOrder(taskId, { itemIds });
      set({ items: data || reordered });
    } catch (err) {
      set({ items: previous });
      throw err;
    }
  },

  clearItems: () => set({ items: [], error: null }),
}));
